import { useCallback, useEffect, useState } from "react";
import { baseUrl } from "../../../store/constant";

/** Laravel score HTML proxy (server-cached ~4s; HTML self-refreshes every 5s). */
const proxyScoreHtmlUrl = (eventId) => {
  const root = String(baseUrl || "").replace(/\/$/, "");
  return `${root}/feed/live-score-html/${eventId}`;
};

const asArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    return v
      .split(/[\s,|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const ballLabel = (b) => {
  if (b == null || b === "") return "-";
  if (typeof b === "object") {
    return String(b.runs ?? b.run ?? b.ball ?? b.value ?? b.result ?? "-");
  }
  return String(b);
};

/** Normalize provider payload — used by home match cards. */
export function normalizeLiveScore(payload) {
  let d = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  if (!d || typeof d !== "object") return null;

  if (d.message && /not found|no data|unavailable/i.test(String(d.message))) return null;
  if (d.model === null) return null;
  if (d.model && typeof d.model === "object") d = d.model;

  const team1 = d.team1 || d.home || {};
  const team2 = d.team2 || d.away || {};

  const batsmen = asArr(d.batsman || d.batsmen || d.batsmanList).map((b) => ({
    name: b?.name || b?.playerName || b?.batsmanName || "—",
    run: b?.run ?? b?.runs ?? 0,
    ball: b?.ball ?? b?.balls ?? 0,
    fours: b?.fours ?? b?.["4s"] ?? 0,
    sixes: b?.sixes ?? b?.["6s"] ?? 0,
    sr: b?.strike_rate ?? b?.sr ?? b?.strikeRate ?? "—",
    onStrike: Boolean(b?.onStrike || b?.strike || b?.isOnStrike || b?.active),
  }));

  let bowler = d.bowler || d.currentBowler || null;
  if (!bowler && Array.isArray(d.bowlers) && d.bowlers.length) {
    bowler = d.bowlers.find((x) => x?.active || x?.isBowling) || d.bowlers[0];
  }
  if (typeof bowler === "string") {
    bowler = { name: bowler };
  }

  let currentOver = asArr(
    d.lastBalls || d.thisOver || d.currentOver || d.currentOverBalls || d.balls
  ).map(ballLabel);
  let previousOver = asArr(
    d.previousOver ||
      d.prevOver ||
      d.lastOver ||
      d.lastOverBalls ||
      d.secondLastBalls ||
      d.prevOverBalls
  ).map(ballLabel);

  if (!previousOver.length && currentOver.length > 6) {
    previousOver = currentOver.slice(-12, -6);
    currentOver = currentOver.slice(-6);
  }
  if (currentOver.length > 6) currentOver = currentOver.slice(-6);
  if (previousOver.length > 6) previousOver = previousOver.slice(-6);

  const statusText =
    d.currentBall ||
    d.spn ||
    d.commentary ||
    d.ballStatus ||
    d.statusMessage ||
    d.matchStatus ||
    "";

  const targetText = d.needByBall || d.target || d.required || d.result || "";

  const hasTeams = Boolean(
    team1.name ||
      team1.shortName ||
      team2.name ||
      team2.shortName ||
      d.team1Name ||
      d.team2Name
  );
  if (!hasTeams) return null;

  return {
    team1: {
      name: team1.shortName || team1.name || d.team1Name || "T1",
      flag: team1.flag || "",
      score: team1.score || d.team1Score || "0-0",
      overs: team1.overs ?? d.team1Overs ?? "0",
    },
    team2: {
      name: team2.shortName || team2.name || d.team2Name || "T2",
      flag: team2.flag || "",
      score: team2.score || d.team2Score || "0-0",
      overs: team2.overs ?? d.team2Overs ?? "0",
    },
    batsmen,
    bowler: bowler
      ? {
          name: bowler.name || bowler.bowlerName || bowler.playerName || "—",
          overs: bowler.overs ?? bowler.over ?? "—",
          runs: bowler.runs ?? bowler.run ?? "—",
          wickets: bowler.wickets ?? bowler.wicket ?? bowler.w ?? "—",
          economy: bowler.economy ?? bowler.eco ?? bowler.er ?? "—",
        }
      : null,
    currentOver,
    previousOver,
    statusText: String(statusText),
    targetText: String(targetText),
    crr: d.crr ?? d.CRR ?? d.currentRunRate ?? null,
    rrr: d.rrr ?? d.RRR ?? d.requiredRunRate ?? null,
    lastBall: currentOver.length
      ? currentOver[currentOver.length - 1]
      : d.lastBall || d.recentBall || null,
  };
}

const ClientLiveScore = ({ matchId }) => {
  const [showWidget, setShowWidget] = useState(true);
  const [frameHeight, setFrameHeight] = useState(280);
  const [loadedOnce, setLoadedOnce] = useState(false);

  useEffect(() => {
    setLoadedOnce(false);
    setFrameHeight(280);
  }, [matchId]);

  const fitIframe = useCallback((iframe) => {
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      const body = doc.body;
      const root = doc.documentElement;
      const h = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        root?.scrollHeight || 0,
        root?.offsetHeight || 0
      );
      if (h > 40) {
        setFrameHeight(Math.min(Math.ceil(h + 2), 900));
      }
    } catch {
      // ignore
    }
  }, []);

  if (!matchId) return null;

  return (
    <section className="vivo-score vivo-score--widget" aria-label="Live scorecard">
      <button
        type="button"
        className="vivo-score-toggle"
        onClick={() => setShowWidget((v) => !v)}>
        {showWidget ? "Hide live score" : "Show live score"}
      </button>

      {showWidget ? (
        <div className="vivo-score-iframe-wrap">
          {!loadedOnce ? (
            <div className="vivo-score-empty vivo-score-empty--overlay">Scoreboard loading…</div>
          ) : null}
          <iframe
            key={matchId}
            title="Live score"
            src={proxyScoreHtmlUrl(matchId)}
            className="vivo-score-iframe"
            style={{ height: frameHeight }}
            referrerPolicy="no-referrer"
            onLoad={(e) => {
              setLoadedOnce(true);
              fitIframe(e.currentTarget);
              window.setTimeout(() => fitIframe(e.currentTarget), 250);
            }}
          />
        </div>
      ) : null}
    </section>
  );
};

export default ClientLiveScore;
